import React, { useEffect, useState } from "react";

import { CATALOG_URL, SITE_URL } from "../../config/site";
import SEO from "../../components/SEO";
import Toast from "../../components/Toast";
import ProductSelector from "../../components/ProductSelector";
import CanvasEditor from "../../components/CanvasEditor";
import CollagePhotoEditModal from "../../components/CollagePhotoEditModal";
import QualityModal from "../../components/QualityModal";
import OrderActions from "../../components/OrderActions";
import PreviewCarousel from "../../components/PreviewCarousel";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";
import { FALLBACK_COLLAGE_PRODUCTS } from "../../constants/products";
import { useCollageEditor } from "../../hooks/useCollageEditor";

import "./CollageEditor.css";

export default function CollageEditor() {
  const [products, setProducts] = useState(FALLBACK_COLLAGE_PRODUCTS);
  const [mobileMode, setMobileMode] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  const {
    toast,
    loading,
    progress,
    pieces,
    img,
    scale,
    setScale,
    offset,
    setOffset,
    grid,
    setGrid,
    fileName,
    qualityModal,
    closeQualityModal,
    canvasRef,
    fileInputRef,
    handleUpload,
    handleMouseDown,
    handleMouseUp,
    handleMouseMove,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    continueLowQualityOrder,
    resetEditor,
    handleFinishOrder,
  } = useCollageEditor();

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 920px)");
    const updateMobileMode = () => setMobileMode(mediaQuery.matches);

    updateMobileMode();
    mediaQuery.addEventListener?.("change", updateMobileMode);

    return () => {
      mediaQuery.removeEventListener?.("change", updateMobileMode);
    };
  }, []);

  useEffect(() => {
    async function loadProducts() {
      try {
        const response = await fetch(CATALOG_URL);
        const data = await response.json();

        const collageProducts = data.products
          .filter((product) => product.type === "collage" && product.active)
          .sort((a, b) => a.order - b.order);

        setProducts(collageProducts);
      } catch (error) {
        console.error("Error loading products.json", error);
      }
    }

    loadProducts();
  }, []);

  useEffect(() => {
    if (!products.length) return;

    setGrid((currentGrid) => {
      const exists = products.some((product) => product.id === currentGrid.id);

      return exists ? currentGrid : products[0];
    });
  }, [products, setGrid]);

  return (
    <>
      <SEO
        title="Collage magnético personalizado con tus fotos | Imalandia"
        description="Convierte una foto especial en un collage magnético dividido en piezas de 5x5 cm, listo para decorar o regalar."
        canonical={`${SITE_URL}/editor`}
      />

      <Header />

      <main className="collage-page">
        <h1 className="title">Crea tu collage</h1>

        <Toast message={toast} />

        <section className="collage-flow">
          <div className="collage-quality-note">
            <strong>Consejo de calidad</strong>

            <p>
              Usa una foto con buena resolucion. Evita ampliar demasiado la
              imagen para que el collage final mantenga buena definicion.
            </p>
          </div>

          <div
            className={
              pieces.length
                ? "collage-editor-layout has-previews"
                : "collage-editor-layout"
            }
          >
            <aside className="collage-editor-controls">
              <ProductSelector
                products={products}
                grid={grid}
                setGrid={setGrid}
              />
            </aside>

            <div className="collage-editor-main">
              <CanvasEditor
                canvasRef={canvasRef}
                fileName={fileName}
                handleUpload={handleUpload}
                inputRef={fileInputRef}
                mobileMode={mobileMode}
                onOpenEditor={() => setEditorOpen(true)}
                handleMouseDown={handleMouseDown}
                handleMouseUp={handleMouseUp}
                handleMouseMove={handleMouseMove}
                handleTouchStart={handleTouchStart}
                handleTouchMove={handleTouchMove}
                handleTouchEnd={handleTouchEnd}
              />
            </div>

            <aside className="collage-editor-previews">
              <PreviewCarousel pieces={pieces} />
            </aside>

            <aside className="collage-editor-actions">
              <OrderActions
                loading={loading}
                progress={progress}
                onReset={resetEditor}
                onFinishOrder={handleFinishOrder}
                submitLabel="Anadir al carrito"
              />
            </aside>
          </div>

          <QualityModal
            open={qualityModal.open}
            type={qualityModal.type}
            onClose={closeQualityModal}
            onContinue={continueLowQualityOrder}
          />
        </section>
      </main>

      {mobileMode && editorOpen && img && canvasRef.current && (
        <CollagePhotoEditModal
          image={img}
          width={canvasRef.current.width}
          height={canvasRef.current.height}
          grid={grid}
          initialScale={scale}
          initialOffset={offset}
          onClose={() => setEditorOpen(false)}
          onConfirm={({ scale: nextScale, offset: nextOffset }) => {
            setScale(nextScale);
            setOffset(nextOffset);
            setEditorOpen(false);
          }}
          onChangeImage={(file) => {
            handleUpload({
              target: {
                files: [file],
              },
            });
          }}
        />
      )}

      <Footer />
    </>
  );
}
